export const piicHero = {
  badge: "Flagship Programme",
  title: "Partnership for Implementation of Innovation Circles",
  subtitle:
    "PIIC is TIE's foundational 16-week factory-embedded innovation operating system. It installs 2-4 Innovation Circles inside a factory, each with 6 cross-functional members, to identify bottlenecks, prototype solutions, and produce evidence-backed transformation.",
  ctaPrimary: "Enrol Your Factory",
  ctaSecondary: "See How It Works",
};

export const piicWhatIs = {
  title: "What is PIIC?",
  paragraphs: [
    "PIIC - the Partnership for Implementation of Innovation Circles - is a structured, time-bound programme that embeds innovation capacity directly inside textile and apparel factories. Over 16 weeks, cross-functional teams work through a proven methodology to identify operational pain points, test solutions on the factory floor, and document measurable improvements.",
    "Each PIIC deployment installs 2 to 4 Innovation Circles, with 6 members per circle drawn from different departments - engineering, production, quality, maintenance, and management. This cross-functional design breaks down silos and ensures solutions are practical, not theoretical.",
    "PIIC is not consulting. It is co-creation. The factory's own people generate the ideas, run the experiments, and own the results. TIE provides the methodology, facilitation, expert access, and validation framework.",
  ],
  highlights: [
    { label: "Duration", value: "16 Weeks" },
    { label: "Circles per Factory", value: "2–4" },
    { label: "Members per Circle", value: "6" },
    { label: "Factories Served", value: "80+" },
  ],
};

export const piicProcessSteps = [
  {
    phase: "Phase 1",
    week: "Weeks 1–2",
    title: "Discovery & Circle Formation",
    description:
      "Factory leadership and TIE facilitators conduct a readiness assessment. Innovation Circles are formed with 6 cross-functional members each. Baseline data is collected for key performance indicators.",
    activities: [
      "Readiness assessment & stakeholder alignment",
      "Circle member selection & onboarding",
      "Baseline KPI documentation",
      "Problem area prioritization workshop",
    ],
  },
  {
    phase: "Phase 2",
    week: "Weeks 3–8",
    title: "Ideation & Solution Prototyping",
    description:
      "Circles identify bottlenecks through structured observation and data analysis. Ideas are generated, shortlisted, and prototyped on the factory floor under real production conditions.",
    activities: [
      "Bottleneck identification & root-cause analysis",
      "Cross-circle idea generation sessions",
      "Rapid prototyping of top 2–3 solutions",
      "Weekly progress reviews with TIE facilitators",
    ],
  },
  {
    phase: "Phase 3",
    week: "Weeks 9–12",
    title: "Testing & Validation",
    description:
      "Promising solutions enter a structured testing phase. Before-and-after metrics are captured. Solutions are refined based on real production feedback until they meet the evidence threshold.",
    activities: [
      "Pilot testing under live production conditions",
      "Before-and-after metric capture",
      "Solution refinement & iteration",
      "Risk assessment & mitigation planning",
    ],
  },
  {
    phase: "Phase 4",
    week: "Weeks 13–16",
    title: "Documentation & Scale Planning",
    description:
      "Circles compile comprehensive evidence packs with quantitative results, SOP drafts, and scale-up roadmaps. A closing ceremony presents findings to factory leadership and TIE validates outcomes.",
    activities: [
      "Evidence pack compilation with metrics",
      "SOP drafting for validated solutions",
      "Scale-up roadmap & resource planning",
      "Closing presentation & certification",
    ],
  },
];

export const piicCells = [
  {
    name: "Textile Engineering Cell",
    icon: "ri-settings-3-line",
    focus: [
      "Machine optimization & process engineering",
      "Automation feasibility studies",
      "Technical specification development",
      "Engineering-driven efficiency gains",
    ],
  },
  {
    name: "Management Excellence Cell",
    icon: "ri-briefcase-4-line",
    focus: [
      "Organizational transformation & change discipline",
      "Performance management frameworks",
      "Decision routines & governance",
      "Leadership capacity for innovation",
    ],
  },
  {
    name: "Textile Processing Cell",
    icon: "ri-drop-line",
    focus: [
      "Dyeing & finishing process optimization",
      "Water, steam & energy reduction",
      "Quality stability & RFT improvement",
      "Sustainable processing innovation",
    ],
  },
  {
    name: "Textile Materials Cell",
    icon: "ri-stack-line",
    focus: [
      "New fiber & yarn development",
      "Blended & technical textile materials",
      "Pre-consumer waste recycling",
      "Material diversification & MMF transition",
    ],
  },
  {
    name: "Marketing & Merchandising Cell",
    icon: "ri-global-line",
    focus: [
      "Supply chain operation synchronization",
      "Costing standardization & value engineering",
      "Buyer evaluation & market intelligence",
      "Product positioning for global markets",
    ],
  },
  {
    name: "Textile Efficiency Cell",
    icon: "ri-speed-line",
    focus: [
      "Lean management & waste elimination",
      "Resource efficiency optimization",
      "Lead-time compression & flow optimization",
      "Preventive maintenance & planning discipline",
    ],
  },
];

export const piicOutcomes = {
  title: "What Every Factory Receives",
  items: [
    {
      title: "Evidence Pack",
      description:
        "A comprehensive document with before-and-after metrics, process documentation, and photographic evidence of every solution tested.",
      icon: "ri-file-list-3-line",
    },
    {
      title: "Installed Innovation Circles",
      description:
        "2–4 active, trained Innovation Circles that continue operating after the programme ends, creating a permanent innovation infrastructure.",
      icon: "ri-team-line",
    },
    {
      title: "Standard Operating Procedures",
      description:
        "Draft SOPs for every validated solution, ready for management approval and factory-wide rollout.",
      icon: "ri-book-2-line",
    },
    {
      title: "Certified Circle Members",
      description:
        "Circle members receive certificates of completion, building internal credibility and career development pathways.",
      icon: "ri-medal-line",
    },
    {
      title: "Expert Network Access",
      description:
        "Ongoing access to TIE's 150+ expert platform for specialized guidance beyond the programme period.",
      icon: "ri-links-line",
    },
    {
      title: "Scale-up Roadmap",
      description:
        "A clear roadmap for rolling validated innovations across factory groups, with resource requirements and timelines.",
      icon: "ri-map-2-line",
    },
  ],
};

export const piicServices = [
  "Forming and Culminating an Expert Pool",
  "Workshops for Owners and Top Level Management",
  "Trainings for Mid Level Management",
  "Stakeholders Engagement Programs",
  "Technology Review Summit",
  "Operational Intelligence and Management Tools",
  "Expert Vetting and Reporting Services",
  "Peer Knowledge Exchange Sessions",
];

export const piicStats = [
  { value: "250+", label: "Projects Deployed", sub: "across 80+ factories" },
  { value: "4", label: "Innovation Circles", sub: "per factory on average" },
  { value: "16", label: "Weeks", sub: "structured programme duration" },
  { value: "96%", label: "Completion Rate", sub: "factories finish the programme" },
];